import { MoreHorizontal } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import EmptyState from '@/components/common/EmptyState';
import { CardSkeleton } from '@/components/common/CardSkeleton';
import { useGetBetHistoryQuery } from '@/store/api/betReportApi';
import type { BetHistoryResponse, ParlayBet, ParlayBetLeg } from '@/types/betReportTypes';
import { cn, formatCurrency, formatDate, getNumberValue, getParlayBetTypeLabel } from '@/utils';
import { isParlayBet } from '@/utils/betReportGuards';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

const LIMIT = 100;

interface MyBetsParlayListProps {
  onCountChange?: (count: number) => void;
}

const getProfitLossClass = (value: number) => (value < 0 ? 'text-[#f00000]' : 'text-black');

export default function MyBetsParlayList({ onCountChange }: MyBetsParlayListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryArgs = useMemo(
    () => ({
      page: 1,
      limit: LIMIT,
      betTime: 'CURRENT',
      status: 'Pending',
      marketType: 'parlay',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    []
  );

  const { data, isFetching } = useGetBetHistoryQuery(queryArgs);
  const parlayData = data as BetHistoryResponse<ParlayBet> | undefined;
  const bets = useMemo(() => (parlayData?.bets ?? []).filter(isParlayBet), [parlayData?.bets]);

  useEffect(() => {
    onCountChange?.(parlayData?.pagination?.totalItems ?? bets.length);
  }, [bets.length, onCountChange, parlayData?.pagination?.totalItems]);

  if (isFetching) {
    return (
      <div className="space-y-2 p-3">
        <CardSkeleton length={4} />
      </div>
    );
  }

  if (!bets.length) {
    return <EmptyState className="pt-[8.125rem]" />;
  }

  return (
    <div className="bg-white px-2 pb-4">
      <div className="flex min-h-[3.25rem] items-center justify-end bg-[#eef7fb] px-2">
        <button
          type="button"
          className="flex h-9 min-w-[3.875rem] items-center justify-between rounded-[0.5rem] bg-white px-3 text-[0.875rem] shadow-sm"
        >
          <span>All</span>
          <i className={'icon-arrow-double text-[0.62rem] text-gray-600'} />
        </button>
      </div>

      <div className="space-y-[1.625rem] px-1 pb-4 mt-1">
        {bets.map((bet, index) => {
          const id = String(bet.id ?? `parlay-${index}`);

          return (
            <ParlayModalCard
              key={id}
              bet={bet}
              rowId={`PAR-${id}`}
              open={expandedId === id}
              onToggle={() => setExpandedId((currentId) => (currentId === id ? null : id))}
            />
          );
        })}
      </div>
    </div>
  );
}

function ParlayModalCard({
  bet,
  rowId,
  open,
  onToggle,
}: {
  bet: ParlayBet;
  rowId: string;
  open: boolean;
  onToggle: () => void;
}) {
  const legs = bet.legs ?? [];
  const stake = getNumberValue(bet.stake);
  const odds = getNumberValue(bet.totalOdds);
  const profit = Number(bet.potentialProfit ?? 0);
  const betType = getParlayBetTypeLabel(legs.length);
  const visibleLegs = open ? legs : [];
  const currencySymbol = useCurrencySymbol();

  return (
    <article
      className={cn(
        'relative overflow-visible rounded-[0.438rem] bg-[#e7f1f7] pb-2 text-black',
        open && 'bg-[#a8bdc8]'
      )}
    >
      <div className="flex min-h-[2.25rem] items-center justify-between px-2 text-[0.875rem] leading-none">
        <span>Ref: {rowId}</span>
        <span>{formatDate(bet.placedAt, 'DD-MM-YYYY HH:mm', '')}</span>
      </div>

      <div className="overflow-hidden bg-white">
        <div className="flex min-h-[3.313rem] items-center gap-2 border-b border-[#cbd9e0] px-2">
          <MoreBadge />
          <span className="min-w-0 font-medium text-[1rem] leading-tight">{betType}</span>
        </div>

        <div className="grid grid-cols-3 border-b border-[#cbd9e0]">
          <ValueCell label="Odds">{formatCurrency(odds, '-', 3)}</ValueCell>
          <ValueCell label={`Stake (${currencySymbol})`}>{formatCurrency(stake, '-', 2)}</ValueCell>
          <ValueCell label={`Profit (${currencySymbol})`} valueClassName={getProfitLossClass(profit)}>
            {formatCurrency(Math.abs(profit), '-', 2)}
          </ValueCell>
        </div>

        <div
          className={cn(
            'min-h-[1.875rem] px-2 py-1 text-[0.813rem]',
            open && 'border-b border-[#cbd9e0]'
          )}
        >
          Will be keep at in-play
        </div>

        {visibleLegs.map((leg, index) => (
          <ParlayLegItem key={getParlayLegKey(leg, index)} leg={leg} />
        ))}
      </div>

      {legs.length > 0 && (
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? 'Collapse parlay bet details' : 'Expand parlay bet details'}
          onClick={onToggle}
          className={cn(
            'absolute bottom-[-1.125rem] left-1/2 z-10 flex h-[1.563rem] w-[6.938rem] -translate-x-1/2 items-center justify-center rounded-b-[0.438rem]',
            open ? 'bg-[#a8bdc8]' : 'bg-[#d3e1e8]'
          )}
        >
          <i
            aria-hidden="true"
            className={cn(
              'icon-arrow-down transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </button>
      )}
    </article>
  );
}

const getParlayLegKey = (leg: ParlayBetLeg, index: number) =>
  String(leg.id ?? `${leg.eventId}-${leg.marketName}-${leg.selection}-${index}`);

function ParlayLegItem({ leg }: { leg: ParlayBetLeg }) {
  const status = String(leg.status ?? '').toLowerCase();
  const isLost = status === 'lost';
  const isWon = status === 'won';
  const eventName = leg.eventName ?? leg.marketName ?? `Event ${leg.eventId}`;

  return (
    <div className="border-b border-[#cbd9e0] last:border-b-0">
      <div className="min-h-[2.125rem] bg-[#d3e1e8] px-2 py-1 text-[0.813rem] leading-[1rem]">
        <span>{leg.sport}</span>
        <i className={'icon-triangle-black-600 text-[0.02rem] align-middle'} />
        <span className="font-medium">{eventName}</span>
        <i className={'icon-triangle-black-600  text-[0.02rem] align-middle'} />
        <span>{leg.marketName}</span>
      </div>

      <div className="grid min-h-[3.313rem] grid-cols-[4.25rem_minmax(0,1fr)_auto] items-center gap-2 px-2">
        {isLost ? (
          <span className="shrink-0 rounded-full bg-[#e02a16] text-center px-3 py-2 text-[1rem] font-bold leading-none text-white">
            LOST
          </span>
        ) : isWon ? (
          <span className="shrink-0 rounded-full bg-green-600 text-center px-3 py-2 text-[1rem] font-bold leading-none text-white">
            WON
          </span>
        ) : (
          <MoreBadge />
        )}

        <p className={'font-medium min-w-0 break-words text-center text-[1rem] leading-tight'}>
          {leg.selection}
        </p>

        <span className="shrink-0 rounded-[0.375rem] bg-[#dce8ee] px-3 py-2 text-[1rem] font-bold leading-none">
          {formatCurrency(getNumberValue(leg.odds), '-', 3)}
        </span>
      </div>
    </div>
  );
}

function MoreBadge() {
  return (
    <span className="flex h-[2.125rem] w-[3.875rem] shrink-0 items-center justify-center rounded-full bg-[#d3e1e8] text-[#6f8498]">
      <MoreHorizontal size={27} strokeWidth={3} />
    </span>
  );
}

function ValueCell({
  label,
  children,
  valueClassName,
}: {
  label: string;
  children: string;
  valueClassName?: string;
}) {
  return (
    <div className="min-h-[3.438rem] border-r border-[#cbd9e0] px-2 py-2 last:border-r-0">
      <p className="text-[0.75rem] leading-[0.938rem]">{label}</p>
      <span
        className={cn(
          'mt-[0.125rem] font-medium inline-block text-[1rem] leading-none',
          valueClassName
        )}
      >
        {children}
      </span>
    </div>
  );
}
